import React, { useEffect, useState } from 'react';
import { Check, Gift, Clock, ShieldCheck, Truck, Lock, X } from 'lucide-react';
import { HolographicButton } from './HolographicButton';
import { useCountdown } from '../hooks/useCountdown';
export function ValueStack() {
  const {
    minutes,
    seconds
  } = useCountdown();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const CHECKOUT_URL = 'https://emalia.lat/cart/48340125352186:1';
  // Handle escape key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);
  const bonuses = [{
    id: 1,
    title: 'BONO #1: El Escudo de las 4 PM (Kit Oficina)',
    desc: 'El momento donde el 90% falla, tú lo tendrás bajo control. Te doy el plan exacto para que el antojo de la tarde se encuentre con una pared. Incluye: Semáforo de Antojos y Menú SOS de escritorio.',
    image: 'https://cdn.magicpatterns.com/uploads/bhBTg8ve9SW8oSPCKxbj1u/PORTADA_BONO_1.png'
  }, {
    id: 2,
    title: 'BONO #2: Tinto en Paz (Ritual Sin Azúcar)',
    desc: 'Disfruta tu café colombiano como siempre, pero sin que sea el disparador de una recaída. Recupera el placer del ritual sin el drama de la galleta. Incluye: Mapa de disparadores y 3 versiones del ritual.',
    image: 'https://cdn.magicpatterns.com/uploads/hL6FJPdxS6CA5KYAiJi1Tn/PORTADA_BONO_2.png'
  }, {
    id: 3,
    title: 'BONO #3: Radar de Azúcar (Cero Engaños)',
    desc: 'Conviértete en una experta detectando el veneno escondido en el supermercado. Una guía rápida para mercar en 1 minuto sin que el marketing te engañe. Incluye: Checklist palabras trampa y Swaps rápidos de supermercado.',
    image: 'https://cdn.magicpatterns.com/uploads/bmEdCU5qEVpf1vs9RCFneb/PORTADA_BONO_3.png'
  }];
  return <>
      <section id="offer" className="py-12 bg-gradient-to-b from-white to-gray-50" data-id="element-242">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-243">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden relative" data-id="element-244">
            {/* Top Badge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-accent-coral text-white px-6 py-1 rounded-b-lg font-bold text-sm shadow-md z-10" data-id="element-245">
              OFERTA LIMITADA
            </div>

            <div className="p-6 md:p-10 pt-12" data-id="element-246">
              <h2 className="text-3xl md:text-4xl font-extrabold text-center text-secondary mb-2" data-id="element-247">
                Todo lo que necesitas para tu reset
              </h2>
              <p className="text-center text-gray-500 mb-8" data-id="element-248">
                Acceso inmediato + Bonos de regalo
              </p>

              {/* Main Content Grid */}
              <div className="grid md:grid-cols-2 gap-8 mb-8" data-id="element-249">
                {/* Left: What you get */}
                <div className="space-y-4" data-id="element-250">
                  <h3 className="font-bold text-lg text-secondary border-b border-gray-100 pb-2 mb-4" data-id="element-251">
                    Lo que recibes hoy:
                  </h3>

                  <div className="flex items-start gap-3" data-id="element-252">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5" data-id="element-253">
                      <Check className="h-4 w-4 text-primary" data-id="element-254" />
                    </div>
                    <div data-id="element-255">
                      <span className="font-bold text-gray-800 block" data-id="element-256">
                        Protocolo completo 14 días
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-257">
                        Paso a paso detallado
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-258">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5" data-id="element-259">
                      <Check className="h-4 w-4 text-primary" data-id="element-260" />
                    </div>
                    <div data-id="element-261">
                      <span className="font-bold text-gray-800 block" data-id="element-262">
                        Guía de comida real en Colombia
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-263">
                        Marcas y productos locales
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3" data-id="element-264">
                    <div className="bg-green-100 p-1 rounded-full mt-0.5" data-id="element-265">
                      <Check className="h-4 w-4 text-primary" data-id="element-266" />
                    </div>
                    <div data-id="element-267">
                      <span className="font-bold text-gray-800 block" data-id="element-268">
                        Checklist/Tracker diario
                      </span>
                      <span className="text-xs text-gray-500" data-id="element-269">
                        Solo 2 minutos al día
                      </span>
                    </div>
                  </div>
                </div>

                {/* Right: Bonuses */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 flex flex-col" data-id="element-270">
                  <h3 className="font-bold text-lg text-accent-coral flex items-center gap-2 mb-4" data-id="element-271">
                    <Gift className="h-5 w-5" data-id="element-272" />
                    Bonos GRATIS:
                  </h3>

                  <ul className="space-y-4 flex-grow" data-id="element-273">
                    {bonuses.map(bonus => <li key={bonus.id} className="flex items-center gap-3" data-id="element-274">
                        <img src={bonus.image} alt={bonus.title} className="w-12 h-14 object-contain rounded shadow-sm" data-id="element-275" />
                        <span className="font-medium text-gray-700 text-sm leading-tight" data-id="element-276">
                          {bonus.title}
                        </span>
                      </li>)}
                  </ul>

                  <div className="mt-4 text-center" data-id="element-277">
                    <button onClick={() => setIsModalOpen(true)} className="text-accent-coral underline text-sm font-medium hover:text-accent-coral/80 transition-colors" data-id="element-278">
                      Leer más...
                    </button>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 text-center" data-id="element-279">
                    <p className="text-xs text-gray-500" data-id="element-280">
                      Valor real de los bonos:
                    </p>
                    <p className="text-gray-400 line-through font-medium" data-id="element-281">
                      $105.000 COP
                    </p>
                  </div>
                </div>
              </div>

              {/* Price Section */}
              <div className="bg-secondary/5 rounded-2xl p-6 text-center border border-secondary/10 mb-8" data-id="element-282">
                <div className="flex justify-center items-center gap-2 mb-2" data-id="element-283">
                  <span className="text-gray-500 line-through text-lg" data-id="element-284">
                    $230.000
                  </span>
                  <span className="bg-accent-coral text-white text-xs font-bold px-2 py-1 rounded-md" data-id="element-285">
                    -87% OFF
                  </span>
                </div>

                <div className="flex items-baseline justify-center gap-1 mb-2" data-id="element-286">
                  <span className="text-5xl md:text-6xl font-extrabold text-secondary tracking-tight" data-id="element-287">
                    $29.999
                  </span>
                  <span className="text-xl font-bold text-gray-500" data-id="element-288">COP</span>
                </div>

                <div className="flex items-center justify-center gap-2 text-red-600 font-medium text-sm bg-white inline-flex px-4 py-1 rounded-full shadow-sm border border-red-100" data-id="element-289">
                  <Clock className="h-4 w-4" data-id="element-290" />
                  <span data-id="element-291">
                    La oferta termina en: {minutes}:
                    {seconds < 10 ? `0${seconds}` : seconds}
                  </span>
                </div>
              </div>

              {/* CTA */}
              <div className="space-y-6" data-id="element-292">
                <HolographicButton href={CHECKOUT_URL} fullWidth className="text-xl py-5 shadow-2xl shadow-primary/30" firePixelEvent={true} data-id="element-293">
                  QUIERO COMENZAR YA
                </HolographicButton>

                {/* Visual Trust Badges */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-100 pt-6" data-id="element-294">
                  <div className="flex flex-col items-center text-center gap-2" data-id="element-295">
                    <div className="w-10 h-10 bg-green-50 rounded-full flex items-center justify-center text-green-600" data-id="element-296">
                      <Lock className="h-5 w-5" data-id="element-297" />
                    </div>
                    <div data-id="element-298">
                      <p className="font-bold text-xs text-gray-800" data-id="element-299">
                        Pago 100% Seguro
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-300">
                        Encriptación SSL
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center gap-2" data-id="element-301">
                    <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center text-blue-600" data-id="element-302">
                      <Truck className="h-5 w-5" data-id="element-303" />
                    </div>
                    <div data-id="element-304">
                      <p className="font-bold text-xs text-gray-800" data-id="element-305">
                        Entrega Inmediata
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-306">
                        Acceso por Email
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col items-center text-center gap-2" data-id="element-307">
                    <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center text-amber-600" data-id="element-308">
                      <ShieldCheck className="h-5 w-5" data-id="element-309" />
                    </div>
                    <div data-id="element-310">
                      <p className="font-bold text-xs text-gray-800" data-id="element-311">
                        Garantía Total
                      </p>
                      <p className="text-[10px] text-gray-500" data-id="element-312">
                        7 Días de prueba
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="flex justify-center gap-4 opacity-60 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-313">
                  <img src="https://cdn.magicpatterns.com/uploads/kghmG89Hkaibmwg1RnVYri/Visa_logo.png" alt="Visa" className="h-5 w-auto object-contain" data-id="element-314" />
                  <img src="https://cdn.magicpatterns.com/uploads/wtSaJSJvffJxG6juw3kUSJ/Mastercard_logo.png" alt="Mastercard" className="h-5 w-auto object-contain" data-id="element-315" />
                  <img src="https://cdn.magicpatterns.com/uploads/6bzYwiAPHy4Q5xVwWnqUK9/Nequi_logo.png" alt="Nequi" className="h-5 w-auto object-contain" data-id="element-316" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Details Modal */}
      {isModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in" data-id="element-317">
          {/* Backdrop click area */}
          <div className="absolute inset-0" onClick={() => setIsModalOpen(false)} data-id="element-318"></div>

          <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up" data-id="element-319">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 p-2 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors z-10" aria-label="Cerrar" data-id="element-320">
              <X className="h-5 w-5" data-id="element-321" />
            </button>

            <div className="p-6 md:p-8" data-id="element-322">
              <div className="text-center mb-8 border-b border-gray-100 pb-6" data-id="element-323">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-accent-coral/10 text-accent-coral rounded-full mb-3" data-id="element-324">
                  <Gift className="h-6 w-6" data-id="element-325" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-secondary" data-id="element-326">
                  Bonos Exclusivos de Lanzamiento
                </h2>
                <p className="text-gray-500 mt-2" data-id="element-327">
                  Detalles de todo lo que te llevas GRATIS hoy
                </p>
              </div>

              <div className="space-y-10" data-id="element-328">
                {bonuses.map((bonus, index) => <div key={bonus.id} className={index !== bonuses.length - 1 ? 'border-b border-gray-100 pb-10' : ''} data-id="element-329">
                    <div className="flex flex-col md:flex-row gap-6 items-center md:items-start" data-id="element-330">
                      <div className="w-full md:w-1/3 flex justify-center" data-id="element-331">
                        <img src={bonus.image} alt={bonus.title} className="w-40 md:w-full max-h-48 object-contain rounded-lg shadow-md" data-id="element-332" />
                      </div>
                      <div className="w-full md:w-2/3 text-center md:text-left" data-id="element-333">
                        <h3 className="text-xl font-bold text-secondary mb-3" data-id="element-334">
                          {bonus.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed" data-id="element-335">
                          {bonus.desc}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 text-center" data-id="element-336">
                <HolographicButton onClick={() => setIsModalOpen(false)} className="w-full md:w-auto" data-id="element-337">
                  Entendido, quiero mi reset
                </HolographicButton>
              </div>
            </div>
          </div>
        </div>}
    </>;
}